import {Component, Input, ViewChildren, ViewChild, QueryList, ElementRef, AfterViewInit, NgZone, OnInit} from '@angular/core';
import { AgmInfoWindow, AgmMarker } from '@agm/core';

import { GoogleService } from '../../google/google.service';
import { Airport } from '../models/airport.model';
import { LocalAttraction } from '../models/local-attraction.model';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { MatDialog, MatDialogConfig, MatSnackBar } from '@angular/material';
import { AirportService } from '../airport.service';
import { EditLocalAttractionComponent } from '../edit-local-attraction/edit-local-attraction.component';
import { DeleteLocalAttractionComponent } from '../delete-local-attraction/delete-local-attraction.component';


@Component({
  selector: 'app-local-attractions',
  templateUrl: './local-attractions.component.html',
  styleUrls: ['./local-attractions.component.scss']
})
export class LocalAttractionsComponent implements OnInit, AfterViewInit {
  @Input() airport: Airport;
  @ViewChild('listEl') listEl: ElementRef;
  @ViewChildren('attractionEl', { read: ElementRef }) attractionCardEls: QueryList<ElementRef>;
  @ViewChild('airportMarker', { read: AgmMarker }) airportMarker: AgmMarker;
  @ViewChild('airportInfoWindow', { read: AgmInfoWindow }) airportInfoWindow: AgmInfoWindow;
  @ViewChildren('attractionMarker', { read: AgmMarker }) attractionMarkers: QueryList<AgmMarker>;
  @ViewChildren('attractionInfoWindow', { read: AgmInfoWindow }) attractionInfoWindows: QueryList<AgmInfoWindow>;
  latLngBounds: any;
  selectedAttraction: LocalAttraction;
  private $containerEl;
  editModeSet: boolean;

  constructor(private _google: GoogleService,
              private _zone: NgZone,
              private _toggleService: EditToggleService,
              public _snackBar: MatSnackBar,
              private _editAttractionDialog: MatDialog,
              private _deleteAttractionDialog: MatDialog,
              private _airportService: AirportService) {}

  /**
   * Returns a list of local attractions that have location.
   */
  get attractions(): Array<LocalAttraction> {
    return this.airport.localAttractions
      .filter(attr => attr.location && attr.location.lat && attr.location.lon);
  }

  ngOnInit() {
    this.editModeSet = this._toggleService.getState();
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
  }

  async ngAfterViewInit() {
    await this._google.promise;
    if (this.attractions.length < 1) {
      return;
    }
    this.latLngBounds = new google.maps.LatLngBounds();
    this.attractions.forEach((attraction) => {
      this.latLngBounds.extend(new google.maps.LatLng(attraction.location.lat, attraction.location.lon));
    });
    // @ts-ignore
    this.$containerEl = $(this.listEl.nativeElement);
  }

  getIconType(attraction: LocalAttraction): string {
    switch (attraction.travelType) {
      case 'Restaurant Nearby':
      case 'Restaurant On Field':
        return 'restaurant';
      case 'Accommodations':
        return 'lodging';
      case 'Transportation':
        return 'taxi';
      case 'Attractions':
        switch (attraction.travelRef) {
          case 'Museum':
            return 'museum';
          case 'Golf':
            return 'golf';
          case 'National Park':
          case 'State or Local Park':
            return 'park';
        }
    }
    return 'attraction';
  }

  getAddress(attraction: LocalAttraction): string {
    let displayAddress = '';
    if (attraction.street1) {
      displayAddress = attraction.street1;
    }
    if (attraction.city) {
      displayAddress = displayAddress + ', ' + attraction.city;
    }
    if (attraction.state) {
      displayAddress = displayAddress + ', ' + attraction.state;
    }
    if (attraction.zip) {
      displayAddress = displayAddress + ' ' + attraction.zip;
    }
    /* if (attraction.country) {
      displayAddress = displayAddress + ', ' + attraction.country;
    } */
    return (displayAddress);
  }

  getWebsite(attraction: LocalAttraction): string {
    if (attraction.website.startsWith('http://') || attraction.website.startsWith('https://')) {
      return (attraction.website);
    } else {
      return ('http://' + attraction.website);
    }
  }

  /**
   * Returns a jQuery element container for the given attraction.
   */
  getAttractionEl(attraction: LocalAttraction) {
    const i = this.attractions.indexOf(attraction);
    if (i < 0) {
      return null;
    }
    const el = this.attractionCardEls.toArray()[i];
    // @ts-ignore
    return $(el.nativeElement);
  }

  /**
   * Scrolls the attractions list to the specified attraction, marking it as selected.
   */
  public scrollToAttraction(attraction: LocalAttraction) {
    const $el = this.getAttractionEl(attraction);
    if (!$el) {
      return;
    }
    const position = $el[0].offsetTop;
    this.$containerEl.animate({ scrollTop: position }, 500);
  }

  /**
   * Hover handler for attraction cards in the list
   */
  public onAttractionItemHover(attraction: LocalAttraction, hover: boolean) {
    this._closeAllInfoWindows();
    if (hover) {
      this.selectedAttraction = attraction;
      this._toggleAttractionInfoWindow(attraction, hover);
    } else {
      this.selectedAttraction = null;
    }
  }

  onAirportMarkerClick() {
    this.infoWindowOpen(this.airportInfoWindow, this.airportMarker);
  }

  onAirportMarkerHover(hover: boolean) {
    if (hover) {
      this.infoWindowOpen(this.airportInfoWindow, this.airportMarker);
    } else {
      this.infoWindowClose(this.airportInfoWindow);
    }
  }

  onAttractionMarkerHover(attraction: LocalAttraction, hover: boolean) {
    this._toggleAttractionInfoWindow(attraction, hover);
    if (hover) {
      this.selectedAttraction = attraction;
      this.scrollToAttraction(attraction);
    } else {
      this.selectedAttraction = null;
    }
  }

  onAttractionMarkerClick(attraction: LocalAttraction) {
    this._toggleAttractionInfoWindow(attraction, true);
  }

  private _closeAllInfoWindows() {
    this.infoWindowClose(this.airportInfoWindow);
    this.attractions.forEach(attr => this._toggleAttractionInfoWindow(attr, false));
  }

  private _toggleAttractionInfoWindow(attraction: LocalAttraction, open: boolean) {
    const i = this.attractions.indexOf(attraction);
    if (i < 0) {
      return;
    }
    const marker = this.attractionMarkers.toArray()[i];
    const window = this.attractionInfoWindows.toArray()[i];
    if (open) {
      this.infoWindowOpen(window, marker);
    } else {
      this.infoWindowClose(window);
    }
  }

  infoWindowOpen(infoWindow: AgmInfoWindow, marker: AgmMarker = null) {
    infoWindow.hostMarker = marker;
    // Run outside angular to avoid triggering an endless cycle of change detection:
    // https://github.com/SebastianM/angular-google-maps/issues/796
    this._zone.runOutsideAngular(() => {
      infoWindow.open();
    });
  }

  infoWindowClose(infoWindow: any) {
    this._zone.runOutsideAngular(() => {
      infoWindow.close();
    });
  }

  async reloadAirport(identifier: string) {
    this.airport = await this._airportService.getAirport(identifier, true);
  }

  openEditAttractionDialog(index: number) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '60%';
    dialogConfig.data = {
      airID: this.airport.airportId,
      localAttraction: null,
      isNew: false,
      airportLocation: this.airport.location
    };
    index == null ? dialogConfig.data.isNew = true : dialogConfig.data.localAttraction = this.airport.localAttractions[index];
    dialogConfig.data['airID'] = this.airport.airportId;
    dialogConfig.width = '60%';
    const dialogRef = this._editAttractionDialog.open(EditLocalAttractionComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      this.reloadAirport(this.airport.identifier);
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        if (isUpdated.isNew) {
          // this.airport.pilotControlledLighting.push(isUpdated.pcl);
          // this.reloadAirport(this.airport.identifier);
          this._snackBar.open('Local Attraction', 'Added', {
            duration: 2000
          });
        } else {
          this._snackBar.open('Local Attraction', 'Updated', {
            duration: 2000
          });
        }
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  deleteAttraction(index: number) {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      airportId: this.airport.airportId,
      localAttractionId: this.attractions[index].id
    };
    const dialogRef = this._deleteAttractionDialog.open(DeleteLocalAttractionComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this._snackBar.open('Local Attraction', 'Deleted', {
          duration: 2000
        });
        // this.airport.localAttractions.splice(index, 1);
        this.reloadAirport(this.airport.identifier);
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }
}
